export type InferenceAppId = string;

export type GraphClawOption = {
	id: string;
	name: string;
	enabled: boolean;
};

export type GraphClawSourceOption = GraphClawOption & {
	matchers: GraphClawOption[];
};

export type GraphClawCatalogFilter = 'entities' | 'relationships';

export type GraphClawTargetRequirement = 'none' | 'optional' | 'required';

export type GraphClawResultKind = 'entity' | 'relationship';

export type GraphClawTargetUsage = 'none' | 'scope' | 'candidates';

export type GraphClawDestination = 'shadow' | 'graph';

export type GraphClawAiConfiguration = {
	useCase: string;
	model: string;
	reasoningLevel: string;
};

export type GraphClawInferDefinition = GraphClawOption & {
	description: string;
	resultKind: GraphClawResultKind;
	targetRequirement: GraphClawTargetRequirement;
	targetUsage: GraphClawTargetUsage;
	compatibleTargetIds: string[];
	compatibleSourceIds: string[];
	supportedDestinations: GraphClawDestination[];
	graphAvailable?: boolean;
	graphSources?: string[];
};

export type GraphClawMockInferDefinition = GraphClawInferDefinition;

export type GraphClawMockTargetCapability = GraphClawOption & {
	description: string;
};

export type GraphClawMockSource = GraphClawOption & {
	description: string;
};

export type GraphClawV2Configuration = {
	name: string;
	source: string;
	infer: string;
	forTargets: string[];
	destination: GraphClawDestination;
	additionalPrompt: string;
	ai: GraphClawAiConfiguration;
	enabled: boolean;
	realtime: boolean;
};

export type GraphClawApiVersion = 'v1' | 'v2';

export type GraphClawErrorKind =
	| 'dependency'
	| 'duplicate'
	| 'not-found'
	| 'permission'
	| 'request-conflict'
	| 'unavailable-capability'
	| 'validation'
	| 'unknown';

export type GraphClawHubView =
	| 'inference-details'
	| 'dashboard'
	| 'inferences'
	| 'apps'
	| 'app-detail'
	| 'review'
	| 'my-inference'
	| 'registry'
	| 'audit-log'
	| 'try-it';

export type GraphClawInferenceAppPermissions = {
	canView: boolean;
	canManage: boolean;
	canRunInference: boolean;
};

export type GraphClawInferenceQualityMetricValues = {
	taskRuns: number | null;
	tasksSucceeded: number | null;
	tasksFailed: number | null;
	successRate: number | null;
	sourceCountReportedRuns: number | null;
	sourceResultCountSum: number | null;
	inputEmptyRuns: number | null;
	inputEmptyRate: number | null;
	resultsGeneratedSum: number | null;
	outputEmptyRuns: number | null;
	outputEmptyRate: number | null;
	claimMetricsReportedRuns: number | null;
	claimsGeneratedSum: number | null;
	claimsWithEvidenceSum: number | null;
	claimsWithoutEvidenceSum: number | null;
	evidenceCoverageRate: number | null;
	schemaCheckedRuns: number | null;
	schemaValidRuns: number | null;
	schemaInvalidRuns: number | null;
	schemaValidRate: number | null;
	tokenUsageReportedRuns: number | null;
	inputTokensSum: number | null;
	outputTokensSum: number | null;
	totalTokensSum: number | null;
	thoughtTokensSum: number | null;
	cachedReadTokensSum: number | null;
	cachedWriteTokensSum: number | null;
	contextTokensSum: number | null;
	taskLatencyP50Ms: number | null;
	taskLatencyP95Ms: number | null;
	responseSizeP50Bytes: number | null;
	responseSizeP95Bytes: number | null;
	confidenceP50: number | null;
	confidenceP95: number | null;
};

export type GraphClawInferenceQualityBucket = {
	bucketStart: string | null;
	finalized: boolean | null;
	dataFreshThrough: string | null;
	metrics: GraphClawInferenceQualityMetricValues | null;
};

export type GraphClawInferenceQualityMetrics = {
	apiVersion: string | null;
	windowStart: string | null;
	windowEnd: string | null;
	resolution: string | null;
	dataFreshThrough: string | null;
	summary: GraphClawInferenceQualityMetricValues;
	buckets: GraphClawInferenceQualityBucket[];
};

export type GraphClawStoredInferenceNode = {
	ari: string;
	entityType: string | null;
	nodeType?: string | null;
	title: string;
	description: string | null;
	url: string | null;
	metadata?: GraphClawStoredDecisionMetadata | null;
};

export type GraphClawStoredDecisionMetadata = {
	kind: string | null;
	subtype: string | null;
	status?: string | null;
	confidenceScore: number | null;
	createdAt: number | null;
	lastUpdated?: number | null;
	decidedBy: string[];
};

export type GraphClawStoredLinkedEntity = {
	ari: string;
	url: string | null;
};

export type GraphClawStoredAnchor = {
	ari: string;
	title: string | null;
	description: string | null;
	url: string | null;
};

export type GraphClawStoredResult = {
	inferenceNode: GraphClawStoredInferenceNode;
	linksEntities: GraphClawStoredLinkedEntity[];
	anchor: GraphClawStoredAnchor | null;
};

export type GraphClawEvalStatus = 'queued' | 'running' | 'ready' | 'failed' | 'timed_out';

export type GraphClawEvalRequest = {
	source_ari: string;
	app_id: InferenceAppId;
	activation_id?: string;
	prompt_template?: string;
};

export type GraphClawEvalError = { code: string; message: string };

export type GraphClawEvalResult = {
	source_ari: string;
	status: Extract<GraphClawEvalStatus, 'ready' | 'failed' | 'timed_out'>;
	normalized_result?: unknown;
	error?: GraphClawEvalError;
};

export type GraphClawEvalStartResult = {
	run_id: string;
	status: Extract<GraphClawEvalStatus, 'queued' | 'running'>;
	expires_at: number;
};

export type GraphClawEvalPromptPreview = {
	prompt_template: string;
	prompt_version: string;
};

export type GraphClawEvalTokenUsage = {
	reported_provider_attempts: number;
	input_tokens: number;
	output_tokens: number;
	total_tokens: number;
	thought_tokens?: number;
	thought_tokens_reported_provider_attempts: number;
	cached_read_tokens?: number;
	cached_read_tokens_reported_provider_attempts: number;
	cached_write_tokens?: number;
	cached_write_tokens_reported_provider_attempts: number;
};

export type GraphClawEvalRunResult = {
	run_id: string;
	status: GraphClawEvalStatus;
	attempt_count: number;
	expires_at: number;
	normalized_result?: unknown;
	token_usage?: GraphClawEvalTokenUsage;
	failure_code?: string;
};

export type GraphClawEvalRecord = {
	clientEvalId: string;
	sourceAri: string;
	app: Pick<InferenceApp, 'id' | 'name'>;
	startedAt: number;
	status: GraphClawEvalStatus;
	runId?: string;
	attemptCount?: number;
	expiresAt?: number;
	normalizedResult?: unknown;
	tokenUsage?: GraphClawEvalTokenUsage;
	error?: GraphClawEvalError;
};

export type GraphClawModelChoice = { id: string; label: string };

export type GraphClawApprovedModelLookupResponse = {
	use_case_id: string;
	infer: string;
	models: GraphClawModelChoice[];
	runtime_selection_supported: boolean;
};

export type GraphClawSaveValidationError = {
	code: string;
	message: string;
	field?: string;
	capabilityId?: string;
};

/** A source system that provides input for an inference app. */
export type GraphClawOptions = {
	products: GraphClawSourceOption[];
	inferenceTargets: GraphClawOption[];
	inferenceTypes: GraphClawOption[];
	inferDefinitions?: GraphClawInferDefinition[];
};

export const emptyGraphClawOptions: GraphClawOptions = {
	products: [],
	inferenceTargets: [],
	inferenceTypes: [],
};

/**
 * A single inference app shown on the GraphClaw Hub.
 *
 * This mirrors the GraphQL app contract exposed by proactive-rag.
 */
export type InferenceApp = {
	id: InferenceAppId;
	name: string;
	product: string;
	inferenceTarget: string;
	inferenceType: string;
	matchers: string[];
	promptOverride: string | null;
	enabled: boolean;
	realtime: boolean;
	available: boolean;
	v2Configuration?: GraphClawV2Configuration;
};

/**
 * A read-only inference app operated outside GraphClaw.
 *
 * This deliberately remains separate from `InferenceApp`: externally managed apps cannot use
 * GraphClaw's detail, enable, edit, or delete experiences.
 */
export type ExternallyManagedInferenceApp = {
	id: string;
	owner: string;
	name: string;
	source: string;
	matchers: string[];
	forTargets: string[];
	infer: string;
};

/**
 * The raw values collected by the add/edit inference app modal.
 * Select values are nullable because catalogs load from GraphQL and may start empty.
 */
export type InferenceAppFormValues = {
	name: string;
	product: string | null;
	inferenceTarget: string | null;
	inferenceType: string | null;
	matchers: string[];
	promptOverride?: string;
	enabled: boolean;
	realtime: boolean;
	v2Configuration?: GraphClawV2Configuration;
};

export type InferenceAppEditValues = {
	name?: string;
	product?: string;
	inferenceTarget?: string;
	inferenceType?: string;
	matchers?: string[];
	promptOverride?: string | null;
	enabled?: boolean;
	realtime?: boolean;
	v2Configuration?: GraphClawV2Configuration;
};

/** Loading lifecycle for the list fetch, mirroring the Workstreams live-data hook. */
export type InferenceAppsStatus = 'loading' | 'ready' | 'empty' | 'error';
